import { EIdentityDocumentType } from '@core/enums/identity-document-type.enum';
import { PageKey } from '@core/enums/page-keys.enum';
import { FlowId, type CheckinWorkflow } from '../workflow-engine.types';
import { identityComparisonFlow } from './identity-comparison.flow';
import { photoIdFlow } from './photo-id.flow';
import { photoSelfieFlow } from './photo-selfie.flow';
import { scanDocumentFlow } from './scan-document.flow';
import { scanGovernmentIdFlow } from './scan-government-id.flow';
import { scanPassportFlow } from './scan-passport.flow';

export enum FacialRecognitionStep {
  Consent = 'Consent',
  ConsentFailure = 'ConsentFailure',
  ConsentBypass = 'ConsentBypass',
  SelectCapture = 'SelectCapture',
  DocumentType = 'DocumentType',
  CaptureDocument = 'CaptureDocument',
  NoDocument = 'NoDocument',
  NoDocumentBypass = 'NoDocumentBypass',
  AfterPassport = 'AfterPassport',
  AfterGovernmentId = 'AfterGovernmentId',
  AfterDocumentScan = 'AfterDocumentScan',
  AfterPhotoId = 'AfterPhotoId',
  PhotoBadgeGate = 'PhotoBadgeGate',
  PhotoSelfie = 'PhotoSelfie',
  AfterPhotoSelfie = 'AfterPhotoSelfie',
  IdentityComparison = 'IdentityComparison',
  AfterIdentityComparison = 'AfterIdentityComparison',
}

export const facialRecognitionFlow: CheckinWorkflow<FacialRecognitionStep> = {
  id: FlowId.FacialRecognition,
  entry: FacialRecognitionStep.Consent,
  steps: {
    [FacialRecognitionStep.Consent]: {
      id: FacialRecognitionStep.Consent,
      page: PageKey.IDENTITY_POLICY,
      next: (variables) =>
        variables.hasConsentedIdentityVerification
          ? { nextStep: FacialRecognitionStep.SelectCapture }
          : { nextStep: FacialRecognitionStep.ConsentFailure },
      back: () => ({ bubble: 'back' }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.ConsentFailure]: {
      id: FacialRecognitionStep.ConsentFailure,
      page: PageKey.IDENTITY_POLICY_FAIL,
      next: () => ({ bubble: 'exit' }),
      back: () => ({ nextStep: FacialRecognitionStep.Consent }),
      bypass: () => ({ nextStep: FacialRecognitionStep.ConsentBypass }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.ConsentBypass]: {
      id: FacialRecognitionStep.ConsentBypass,
      page: PageKey.BYPASS,
      next: () => ({ complete: true }),
      back: () => ({ nextStep: FacialRecognitionStep.ConsentFailure }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.SelectCapture]: {
      id: FacialRecognitionStep.SelectCapture,
      next: (variables) => {
        const configuration = variables.checkinProcessConfiguration;
        if (configuration?.hasPassportScanner) {
          return configuration.hasSkipIdentifyDocumentType
            ? { flow: scanDocumentFlow, returnStep: FacialRecognitionStep.AfterDocumentScan }
            : { nextStep: FacialRecognitionStep.DocumentType };
        }
        return { flow: photoIdFlow, returnStep: FacialRecognitionStep.AfterPhotoId };
      },
      back: () => ({ nextStep: FacialRecognitionStep.Consent }),
    },
    [FacialRecognitionStep.DocumentType]: {
      id: FacialRecognitionStep.DocumentType,
      page: PageKey.DOCUMENT_TYPE,
      next: (variables) => ({
        nextStep:
          variables.identityDocumentType === EIdentityDocumentType.PASSPORT ||
          variables.identityDocumentType === EIdentityDocumentType.GOVERNMENT_ID
            ? FacialRecognitionStep.CaptureDocument
            : FacialRecognitionStep.NoDocument,
      }),
      back: () => ({ nextStep: FacialRecognitionStep.Consent }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.CaptureDocument]: {
      id: FacialRecognitionStep.CaptureDocument,
      next: (variables) => {
        if (variables.identityDocumentType === EIdentityDocumentType.PASSPORT) {
          return { flow: scanPassportFlow, returnStep: FacialRecognitionStep.AfterPassport };
        }
        if (variables.identityDocumentType === EIdentityDocumentType.GOVERNMENT_ID) {
          return {
            flow: scanGovernmentIdFlow,
            returnStep: FacialRecognitionStep.AfterGovernmentId,
          };
        }
        return { nextStep: FacialRecognitionStep.NoDocument };
      },
      back: () => ({ nextStep: FacialRecognitionStep.DocumentType }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.NoDocument]: {
      id: FacialRecognitionStep.NoDocument,
      page: PageKey.NO_IDENTITY_DOCUMENT,
      next: () => ({ bubble: 'exit' }),
      back: () => ({ nextStep: FacialRecognitionStep.DocumentType }),
      bypass: () => ({ nextStep: FacialRecognitionStep.NoDocumentBypass }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.NoDocumentBypass]: {
      id: FacialRecognitionStep.NoDocumentBypass,
      page: PageKey.BYPASS,
      next: () => ({ nextStep: FacialRecognitionStep.PhotoBadgeGate }),
      back: () => ({ nextStep: FacialRecognitionStep.NoDocument }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.AfterPassport]: {
      id: FacialRecognitionStep.AfterPassport,
      next: (variables) =>
        variables.isScanPassportSuccess
          ? { nextStep: FacialRecognitionStep.PhotoSelfie }
          : { nextStep: FacialRecognitionStep.PhotoBadgeGate },
      back: () => ({ nextStep: FacialRecognitionStep.DocumentType }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.AfterGovernmentId]: {
      id: FacialRecognitionStep.AfterGovernmentId,
      next: (variables) =>
        variables.isScanGovernmentIdSuccess
          ? { nextStep: FacialRecognitionStep.PhotoSelfie }
          : { nextStep: FacialRecognitionStep.PhotoBadgeGate },
      back: () => ({ nextStep: FacialRecognitionStep.DocumentType }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.AfterDocumentScan]: {
      id: FacialRecognitionStep.AfterDocumentScan,
      next: (variables) =>
        variables.isScanDocumentSuccess
          ? { nextStep: FacialRecognitionStep.PhotoSelfie }
          : { nextStep: FacialRecognitionStep.PhotoBadgeGate },
      back: () => ({ nextStep: FacialRecognitionStep.SelectCapture }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.AfterPhotoId]: {
      id: FacialRecognitionStep.AfterPhotoId,
      next: () => ({ nextStep: FacialRecognitionStep.PhotoSelfie }),
      back: () => ({ nextStep: FacialRecognitionStep.Consent }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.PhotoBadgeGate]: {
      id: FacialRecognitionStep.PhotoBadgeGate,
      next: (variables) =>
        variables.checkinProcessConfiguration?.hasPhotoOnBadge
          ? { nextStep: FacialRecognitionStep.PhotoSelfie }
          : { complete: true },
    },
    [FacialRecognitionStep.PhotoSelfie]: {
      id: FacialRecognitionStep.PhotoSelfie,
      next: () => ({ flow: photoSelfieFlow, returnStep: FacialRecognitionStep.AfterPhotoSelfie }),
      back: () => ({ nextStep: FacialRecognitionStep.SelectCapture }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.AfterPhotoSelfie]: {
      id: FacialRecognitionStep.AfterPhotoSelfie,
      next: (variables) => {
        const hasSuccessfulCapture =
          variables.isIdPictureSuccess ||
          variables.isScanPassportSuccess ||
          variables.isScanGovernmentIdSuccess ||
          variables.isScanDocumentSuccess;
        return hasSuccessfulCapture
          ? { nextStep: FacialRecognitionStep.IdentityComparison }
          : { complete: true };
      },
    },
    [FacialRecognitionStep.IdentityComparison]: {
      id: FacialRecognitionStep.IdentityComparison,
      next: () => ({
        flow: identityComparisonFlow,
        returnStep: FacialRecognitionStep.AfterIdentityComparison,
      }),
      back: () => ({ nextStep: FacialRecognitionStep.SelectCapture }),
      exit: () => ({ bubble: 'exit' }),
    },
    [FacialRecognitionStep.AfterIdentityComparison]: {
      id: FacialRecognitionStep.AfterIdentityComparison,
      next: () => ({ complete: true }),
    },
  },
};
